import md from 'markdown-ast';
import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
// import { FlexDiv } from '../atoms/MFlexbox';

export const FlexDiv = styled.div`
  display: flex;
  width: ${({ width }) => width};
  height: ${({ height }) => height};
  flex-direction: ${({ flexDir }) => flexDir};
  align-items: ${({ ai }) => ai};
  gap: ${({ gap }) => gap};
  justify-content: ${({ jc }) => jc};
  margin: ${({ margin }) => margin};
  padding: ${({ padding }) => padding};
  flex-wrap: ${({ flexWrap }) => flexWrap};
`;

const polarisStyles = {
  success: '#008060',
  critical: '#D82C0D',
  warning: '#2C6ECB',
  yellow: '#8F6901',
  subdued: '#6D7175',
  'text-inverse': '',
  blue: '#2C6ECB',
  info: '#2C6ECB',
  monochrome: '#000000',
  white: '#FFFFFF',

  center: 'center',
  end: 'right',
  right: 'right',
  start: 'left',
  regular: '400',
  medium: '500',
  semibold: '600',
  bold: '700',

  ceHeadingMd: '1.2rem',
  ceHeading2xl: '1.9rem',

  headingXs: '0.75rem',
  headingSm: '0.875rem',
  headingMd: '1.3rem',
  headingLg: '1.4rem',
  headingXl: '1.6rem',
  heading2xl: '1.75rem',
  heading3xl: '2rem',
  heading4xl: '2.4rem',
  bodySm: '0.75rem',
  bodyMd: '0.875rem',
  bodyLg: '1rem',
  responsiveFontSizes: {
    headingLg: '1rem',
    headingXl: '1.25rem',
    heading2xl: '1.5rem',
    heading3xl: '1.75rem',
    heading4xl: '2rem',
  },
};

export const CETextAlignmentValues = [
  {
    displayValue: 'Left',
    value: 'left',
    actualValue: 'start',
  },
  {
    displayValue: 'Center',
    value: 'center',
    actualValue: 'center',
  },
  {
    displayValue: 'Right',
    value: 'right',
    actualValue: 'end',
  },
];
const polarisTextAlignmentValues = CETextAlignmentValues.reduce((acc, size) => {
  const newAcc = { ...acc };
  newAcc[size.value] = size.actualValue;
  return newAcc;
}, {});
export const getPolarisFontSizeTextAlignmentValue = fontSize =>
  polarisTextAlignmentValues[fontSize];

export function Text({ children, ...props }) {
  const StyledComponent = styled(props.as || 'p')`
    color: ${p => (p.$color ? p.$color : '#202223')};
    text-align: ${p => (p.$alignment ? polarisStyles[p.$alignment] : 'left')};
    font-weight: ${p => (p.$fontWeight ? polarisStyles[p.$fontWeight] : '400')};
    font-size: ${p => (p.$variant ? p.$variant : '1.6rem')};
    font-family: ${p => (p.$fontFamily ? p.$fontFamily : null)};
    margin: ${p => (p.$margin ? p.$margin : 0)};
    padding: ${p => (p.$padding ? p.$padding : null)};
    text-transform: ${p => (p.$textTransform ? p.$textTransform : null)};
    width: ${p => (p.$width ? p.$width : null)};
    min-width: ${p => (p.$minWidth ? p.$minWidth : null)};
    border-bottom: ${p => (p.$borderBottom ? '1px solid #babfc3' : null)};
    cursor: ${p => (p.$cursor ? p.$cursor : 'auto')};
    border-bottom: ${p => (p.$borderBottom ? p.$borderBottom : null)};
    font-style: ${p => (p.$fontStyle ? p.$fontStyle : '')};
    line-height: ${p => p.$lineHeight};
    @media (max-width: 768px) {
      font-size: ${p =>
        p.$variant
          ? polarisStyles.responsiveFontSizes[p.$variant]
          : polarisStyles[p.$variant]};
    }
  `;

  return (
    <StyledComponent
      $fontWeight={props.fontWeight}
      $fontFamily={props.fontFamily}
      $color={props.color}
      $alignment={props.alignment}
      $variant={props.variant}
      $margin={props.margin}
      $padding={props.padding}
      $textTransform={props.textTransform}
      $width={props.width}
      $minWidth={props.minWidth}
      $borderBottom={props.borderBottom}
      $cursor={props.cursor}
      $fontStyle={props.fontStyle}
      $lineHeight={props.lineHeight}
    >
      {children}
    </StyledComponent>
  );
}

const getMdComponentAst = ast => {
  const components = {
    title: {
      component: 'heading',
      type: 'block',
      attributes: [{ key: 'rank', value: 'level' }],
    },
    text: {
      component: 'paragraph',
      type: 'component',
    },
    textWithBlock: {
      component: 'paragraph',
      type: 'block',
    },
    italic: {
      component: 'italic',
      type: 'block',
      properties: {
        fontStyle: 'italic',
      },
    },
    bold: {
      component: 'bold',
      type: 'block',
      properties: {
        fontWeight: '700',
      },
    },
    break: {
      component: 'break',
      type: 'component',
    },
  };
  const componentAst = [];

  // creating a new ast based on our logic to concatenate all the blocks in one single array
  addLineBreaksInMdAst(ast).forEach(node => {
    let nodeType = node.type;
    let componentToUse = components[nodeType];
    // check if component is not found then use default ones
    if (!componentToUse && node && node.block) {
      nodeType = node.block ? 'textWithBlock' : 'text';
      componentToUse = components[nodeType];
    }
    const { properties, attributes, component } = componentToUse;
    // creating a sub node with component and properties
    // sub node is basically a new node with modified properties
    const subNode = { component };

    // looping over all the attributes to extract property from node and reassign with different key
    // eslint-disable-next-line no-unused-expressions
    attributes &&
      attributes.forEach(attribute => {
        subNode[attribute.value] = node[attribute.key];
      });
    if (properties) subNode.props = properties;

    //  we again recursively call the function to get the block nodes
    // blocks are same as of nodes
    if (components[nodeType].type === 'block' && node.block) {
      subNode.blocks = getMdComponentAst(
        node.block.map(block => ({
          ...block,
          // we keep these fields same as parent ones to keep them unique
          // because blocks are normal blocks without any info about parents
          type: nodeType,
          rank: node.rank,
          bullet: node.bullet,
        })),
      );
    } else {
      subNode.content = (node && node.text) || '';
    }
    subNode.bullet = (node && node.bullet) || '';

    componentAst.push(subNode);
  });
  return componentAst;
};

// creates a new ast with line breaks as separate nodes all line breaks are extracted from strings
const addLineBreaksInMdAst = ast => {
  const updatedAst = [];
  ast.forEach((node, i) => {
    // checks for line breaks in the text
    if (node && node.text && node.text.includes('\n')) {
      // split string by line breaks and add each line as a separate node
      const astWithBreaks = node.text
        .split('\n')
        .reduce((acc, content, index) => {
          // first push line break and then content
          if (index) acc.push('\n');
          if (content !== '') acc.push(content);
          return acc;
        }, []);

      // based on line breaks, add break or text nodes
      astWithBreaks.forEach(content => {
        updatedAst.push({
          type: content === '\n' ? 'break' : node.type,
          text: content,
        });
      });
    }
    // checks for list followed by list because it should have a line break after every list item
    else if (
      node.type === 'list' &&
      ast &&
      ast[i + 1] &&
      ast[i + 1].type === 'list'
    ) {
      updatedAst.push(node);
      updatedAst.push({
        type: 'break',
        text: '\n',
      });
    } else updatedAst.push(node);
  });
  return updatedAst;
};

const Heading = props => <Text fontWeight="semibold" {...props} />;

const fontSizeValues = {
  extraSmall: 'headingSm',
  small: 'ceHeadingMd',
  base: 'headingLg',
  medium: 'headingXl',
  large: 'ceHeading2xl',
  extraLarge: 'heading3xl',
};
const textAlignValues = {
  left: 'start',
  center: 'center',
  right: 'end',
};
const colorValues = {
  info: 'subdued',
  warning: 'yellow',
  critical: 'critical',
  success: 'success',
  subdued: 'subdued',
  monochrome: 'monochrome',
  white: 'white',
  blue: 'blue',
};
const MdComponentsConfig = {
  heading: {
    component: Heading,
    additionalProps: {
      level: {
        name: 'variant',
        values: {
          1: 'heading3xl',
          2: 'heading2xl',
          3: 'headingXl',
        },
      },
      alignment: {
        name: 'alignment',
        values: textAlignValues,
      },
      color: {
        name: 'color',
      },
      fontFamily: {
        name: 'fontFamily',
      },
      margin: {
        name: 'margin',
      },
      padding: {
        name: 'padding',
      },
    },
  },
  paragraph: {
    component: Text,
    additionalProps: {
      fontSize: {
        name: 'variant',
        values: fontSizeValues,
      },
      alignment: {
        name: 'alignment',
        values: textAlignValues,
      },
      color: {
        name: 'color',
      },
      fontWeight: {
        name: 'fontWeight',
      },
      fontFamily: {
        name: 'fontFamily',
      },
      margin: {
        name: 'margin',
      },
      padding: {
        name: 'padding',
      },
    },
  },
  italic: {
    component: Text,
    props: {
      fontStyle: 'italic',
    },
    additionalProps: {
      fontSize: {
        name: 'variant',
        values: fontSizeValues,
      },
      alignment: {
        name: 'alignment',
        values: textAlignValues,
      },
      color: {
        name: 'color',
      },
      fontWeight: {
        name: 'fontWeight',
      },
      fontFamily: {
        name: 'fontFamily',
      },
      margin: {
        name: 'margin',
      },
      padding: {
        name: 'padding',
      },
    },
  },
  bold: {
    component: Text,
    props: {
      fontWeight: 'semibold',
    },
    additionalProps: {
      fontSize: {
        name: 'variant',
        values: fontSizeValues,
      },
      alignment: {
        name: 'alignment',
        values: textAlignValues,
      },
      color: {
        name: 'color',
      },
      fontWeight: {
        name: 'fontWeight',
      },
      fontFamily: {
        name: 'fontFamily',
      },
      margin: {
        name: 'margin',
      },
      padding: {
        name: 'padding',
      },
    },
  },
};

const getProps = (componentType, props) => {
  const componentProps = {};
  Object.keys(props).forEach(prop => {
    // based on props from config we extract additional props
    const propConfig =
      MdComponentsConfig &&
      MdComponentsConfig[componentType] &&
      MdComponentsConfig[componentType].additionalProps &&
      MdComponentsConfig[componentType].additionalProps[prop];

    if (propConfig) {
      // if prop exist we assign it to component props
      const propName = propConfig.name;
      componentProps[propName] = propConfig.values
        ? propConfig.values[props[prop]]
        : props[prop];
    }
  });
  // extracting existing props from config
  const existingProps =
    (MdComponentsConfig &&
      MdComponentsConfig[componentType] &&
      MdComponentsConfig[componentType].props) ||
    {};
  return { ...componentProps, ...existingProps };
};

const addSpacesInText = text => text.replaceAll(' ', '\xa0<@wbr<@').split('<@');

// now from our own ast we extract components based on line breaks
const getMdComponentsFromMd = (mdAst, additionalProps = {}) => {
  //  components are 2 level array, f
  // first level is for vertical displaying components
  // and inside first there's nested second level which is used to display components horizontally

  // [              ---> first level vertical
  //   ['level 1']           ---> second level horizontal
  //   ['level 2']
  // ],
  // [
  //  ['level 3']
  // ]

  // it will look something like this in preview
  // level 1 level 2
  // level 3

  const components = [[]];
  // tracking index of second level of array
  let nestedComponentActiveIndex = 0;
  const handleBreak = node => {
    const noOfEmptyLines =
      // - 2 because one break is used to change the line
      node.content.replaceAll('\n', 'b').split('b').length - 2;
    const areMultipleLineBreaks = noOfEmptyLines > 0;

    // if the nested array exist then we push a text element with
    // hidden white color for line break to be visible
    if (components[nestedComponentActiveIndex] && !areMultipleLineBreaks)
      components[nestedComponentActiveIndex].push(
        <div
          style={{
            color: 'transparent',
          }}
        >
          h
        </div>,
      );

    // if there're multiple line breaks then push hidden text elem for each line break
    if (areMultipleLineBreaks)
      for (let index = 0; index < noOfEmptyLines; index += 1) {
        components.push([]);
        // after each line break we increment the index of nested array
        // so it goes to next line
        nestedComponentActiveIndex += 1;
        components[nestedComponentActiveIndex].push(
          <div
            style={{
              color: 'transparent',
            }}
          >
            h
          </div>,
        );
      }

    // at last we again push an empty nested array so all other elements can be in same level
    components.push([]);
    nestedComponentActiveIndex += 1;
  };
  mdAst.forEach(node => {
    // if there're blocks we first concatenate all the blocks in one single array
    if (node.blocks) {
      node.blocks.forEach(block => {
        // handling breaks in block
        if (block.component === 'break') handleBreak(block);
        else {
          const { component: ComponentToUse } = MdComponentsConfig[
            block.component
          ];
          // we extract all the component props from config which component needs
          const props = getProps(block.component, {
            ...block,
            ...additionalProps,
          });
          if (props.variant) {
            props.variant = polarisStyles[props.variant];
          } else {
            props.variant = additionalProps.fontSize;
          }
          // props.variant = additionalProps.fontSize;
          // at last we push nested components in nested array
          components[nestedComponentActiveIndex].push(
            <ComponentToUse {...props}>
              {block.bullet || ''}{' '}
              {addSpacesInText(block.content).map(
                (text, i) => (text === 'wbr' ? <wbr /> : text),
              )}
            </ComponentToUse>,
          );
        }
      });
    } else if (node.component === 'break') {
      handleBreak(node);
    } else {
      // if it's not a block we repeat above steps but for node
      const { component: ComponentToUse } = MdComponentsConfig[node.component];
      const props = getProps(node.component, {
        ...node,
        ...additionalProps,
      });
      if (props.variant) {
        props.variant = polarisStyles[props.variant];
      } else {
        props.variant = additionalProps.fontSize;
      }
      components[nestedComponentActiveIndex].push(
        <ComponentToUse {...props}>
          {node.bullet || ''}{' '}
          {addSpacesInText(node.content).map(
            (text, i) => (text === 'wbr' ? <wbr /> : text),
          )}
        </ComponentToUse>,
      );
    }
  });
  return components;
};

const getMd = (string = '', props, alignment) => {
  let components = [];
  try {
    const ast = md(string); // returns md from library
    const mdd = getMdComponentAst(ast); // modifies md to our own ast
    components = getMdComponentsFromMd(mdd, props); // creating component tree from our mdd ast
  } catch (err) {
    console.error(err);
  }

  return (
    <FlexDiv flexDir="column" ai={alignment} width="100%">
      {components.map(subComponents => (
        // eslint-disable-next-line react/jsx-key
        <FlexDiv jc={alignment} flexWrap="wrap">
          {subComponents.map(component => component)}
        </FlexDiv>
      ))}
    </FlexDiv>
  );
};

const useMd = () => (string, props, alignment) =>
  getMd(string, props, alignment);

export default useMd;

Text.propTypes = {
  children: PropTypes.node.isRequired,
  as: PropTypes.string,
  variant: PropTypes.string,
  color: PropTypes.string,
  fontWeight: PropTypes.string,
  margin: PropTypes.string,
  padding: PropTypes.string,
  alignment: PropTypes.string,
  textTransform: PropTypes.string,
  width: PropTypes.string,
  minWidth: PropTypes.string,
  borderBottom: PropTypes.string,
  cursor: PropTypes.string,
  fontStyle: PropTypes.string,
  lineHeight: PropTypes.string,
  fontFamily: PropTypes.string,
};
